import React, { useState, useEffect, useRef } from 'react';
import ContactForm from './ContactForm';
import { useNavigate } from 'react-router-dom'; // NEW IMPORT


const Navbar = ({ loggedInUser, setLoggedInUser }) => {
  const navigate = useNavigate(); // THIS FIXES THE 'navigate is not defined' ERROR
  const [showContact, setShowContact] = useState(false);
  const navRef = useRef(null);
  const contactWrapRef = useRef(null);

  // Close the Contact Us popover on Escape or a click outside it. The ref wraps
  // both the toggle button AND the popover, so clicking the button counts as
  // "inside" — its own toggle handles open/close and we avoid the classic
  // close-then-reopen race an outside-click handler would otherwise hit.
  useEffect(() => {
    if (!showContact) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setShowContact(false);
    };
    const onMouseDown = (e) => {
      if (contactWrapRef.current && !contactWrapRef.current.contains(e.target)) {
        setShowContact(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [showContact]);

  // 1. Define the sync function
  const syncUserPermissions = async () => {
    if (loggedInUser && loggedInUser._id) {
      try {
        // 1. Try the normal request
        let token = localStorage.getItem('token');
        let response = await fetch(`https://vicktoria.ai/api/${loggedInUser._id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        // 2. IF IT FAILS (403/401) -> TRY REFRESH
        if (response.status === 401 || response.status === 403) {
          console.log("Access Token Expired. Attempting Refresh...");

          // A. Hit the new /refresh endpoint (Credentials include cookies!)
          const refreshResponse = await fetch('https://vicktoria.ai/api/refresh', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include' // CRITICAL: Sends the HTTP-Only Cookie
          });

          if (refreshResponse.ok) {
            // B. We got a new token! Save it.
            const data = await refreshResponse.json();
            localStorage.setItem('token', data.token);
            console.log("Refresh Successful. Retrying Sync...");

            // C. Retry the original request with the NEW token
            response = await fetch(`https://vicktoria.ai/api/${loggedInUser._id}`, {
              headers: {
                'Authorization': `Bearer ${data.token}`,
                'Content-Type': 'application/json'
              }
            });
          } else {
            // D. Real Game Over (Refresh failed too)
            console.log("Refresh Failed. Logging out.");
            setLoggedInUser(null);
            localStorage.removeItem("loggedInUser");
            localStorage.removeItem("token");
            return;
          }
        }

        // 3. Standard Data Processing (same as before)
        const latestData = await response.json();
        if (response.ok && latestData.isSpecialPermission !== loggedInUser.isSpecialPermission) {
          setLoggedInUser(latestData);
          localStorage.setItem("loggedInUser", JSON.stringify(latestData));
        }

      } catch (err) {
        console.error("Navbar sync failed", err);
      }
    }
  };

  // 2. TRIGGER THE SYNC
  useEffect(() => {
    syncUserPermissions();

    // Optional: Set up an interval to check every 30 seconds
    const interval = setInterval(syncUserPermissions, 30000);
    return () => clearInterval(interval);
  }, [loggedInUser?.isSpecialPermission]);

  // 3. SESSION HEARTBEAT — detect cross-app logout within 5 seconds
  // If the user logged out from VicktoriaAi, their sessions are deleted from the shared DB.
  // This lightweight poll detects that and forces logout on this side too.
  useEffect(() => {
    if (!loggedInUser) return;

    const checkSession = async () => {
      try {
        const res = await fetch('https://vicktoria.ai/api/session/check', {
          credentials: 'include'
        });
        if (res.status === 401) {
          setLoggedInUser(null);
          localStorage.removeItem("loggedInUser");
          localStorage.removeItem("token");
          navigate("/");
        }
      } catch (err) {
        // Network error — don't logout on transient failures
      }
    };

    const heartbeat = setInterval(checkSession, 5000);
    return () => clearInterval(heartbeat);
  }, [loggedInUser, setLoggedInUser, navigate]);



  // Mirrors the trial check in Pricing.js and Special.jsx: users within their
  // 14-day free trial window should see the Vicktoria button, not the Pricing button.
  const trialActive = loggedInUser?.accountCreationDate
    ? (() => {
      const trialEnd = new Date(loggedInUser.accountCreationDate);
      trialEnd.setDate(trialEnd.getDate() + 14);
      return new Date() < trialEnd;
    })()
    : false;

  const buttonStyle = {
    backgroundColor: '#4a7094ff',
    color: '#D1DEE6',
    padding: '8px 0',
    borderRadius: '8px',
    fontFamily: 'Inter, sans-serif',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    height: '40px',
    width: '120px',
    margin: '40px 15px 10px',
    position: 'relative',
    zIndex: 22,
    transition: 'all 0.2s ease',
    textAlign: 'center'
  };

  return (
    <>
      <style>
        {`
          /* Bounded at 601px (not just max-width: 1100px) so it can't overlap
             the <=600px phone breakpoint below — the two blocks used to both
             apply at once in the 300-600px range, and since this block's
             declarations are !important, whichever one came later in the
             stylesheet silently won per-property. That's how a stray
             margin (50px 4px 0, meant for this wider tablet layout) was
             leaking into phone-width buttons with no visual justification
             there, and why the "gap" fix below needed !important just to
             win against this block's own !important. */
          @media (min-width: 601px) and (max-width: 1100px) {
            .nav-container { gap: 4px !important; }
            .nav-btn-responsive {
              padding: 8px 0 !important;
              width: 95px !important;
              margin: 50px 4px 0 !important;
              font-size: 11.5px !important;
            }
          }
          
          
          /* Only apply these changes if the screen is 800px or wider */
          @media (min-width: 800px) {
            /* When 'is-open' class is present, override the inline border-radius */
            .contact-btn-responsive.is-open {
              border-bottom-left-radius: 0px !important;
              border-bottom-right-radius: 0px !important;
              /* This makes the button sit flat against the dropdown box below it */
            }
          }

          /* Below 800px the dropdown box detaches from the button (see
             ContactForm's 800px/600px breakpoints) and becomes a centered,
             viewport-width panel, so there's no box edge to stay flush with. */
          @media (max-width: 800px) {
            .contact-btn-responsive.is-open {
              border-radius: 8px !important;
            }
          }
          
          @media (min-width: 300px) and (max-width: 600px) {
            .nav-container {
              display: flex;
              flex-wrap: wrap;
              justify-content: center;
              /* !important: without it this loses to the tablet block's own
                 !important gap on any width where both would apply (they no
                 longer overlap now that block is bounded at 601px+, but kept
                 defensively in case a future breakpoint is added between). */
              gap: 3px !important;
              width: auto;
              max-width: 100%;
            }
            /* Header.js sizes the logo off a nav button's *rendered height*
               (height x 1.4, then scaled up for the PNG's transparent
               padding) — nothing here overrode the base 40px button height,
               so even at phone widths the logo rendered ~108px wide, alone
               consuming over a quarter of a 390px-wide viewport. Explicitly
               shrinking height (not just padding/font-size) is what lets the
               logo shrink too, which is most of what makes 5 buttons fit on
               one row down to ~375px (iPhone SE and up) instead of wrapping.
               margin: 0 clears the tablet block's 50px top margin, which has
               no purpose in this stacked mobile layout. */
            .nav-btn-responsive {
              height: 30px !important;
              padding: 2px 0 !important;
              margin: 0 !important;
              width: 52px !important;
              font-size: 8px !important;
            }
          }

          /* The Contact Us dropdown is positioned flush against the button
             (see ContactForm's containerStyle: top 100% / right 0), which
             only stays flush if the button's own box carries no horizontal
             margin — otherwise that margin becomes part of the wrapping
             div's shrink-to-fit width and the dropdown ends up offset from
             the button by that amount. So horizontal spacing between
             Contact Us and its neighbor lives on .contact-spacer-responsive
             instead, mirroring the same breakpoints as .nav-btn-responsive
             above (15px desktop / 4px tablet / 0 phone). */
          .contact-btn-responsive {
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
          .contact-spacer-responsive { margin: 0 15px; }
          @media (min-width: 601px) and (max-width: 1100px) {
            .contact-spacer-responsive { margin: 0 4px !important; }
          }
          @media (min-width: 300px) and (max-width: 600px) {
            .contact-spacer-responsive { margin: 0 !important; }
          }
        `}
      </style>

      <nav ref={navRef} className="nav-container" style={{ display: 'flex', flexDirection: 'row', gap: '8px', position: 'relative' }}>

        {/* CHANGE: navigate("/") instead of setCurrentView */}
        <button className="nav-btn-responsive" style={buttonStyle} onClick={() => navigate("/about-us")}>About Us</button>
        <button className="nav-btn-responsive" style={buttonStyle} onClick={() => navigate("/news")}>News</button>

        {/* Show Vicktoria button for paid subscribers OR users within their 14-day free trial */}
        {(loggedInUser?.isSpecialPermission || trialActive) ? (
          <button
            className="nav-btn-responsive"
            style={{ ...buttonStyle, backgroundColor: '#5a8ab8' }}
            onClick={async () => {
              try {
                const token = localStorage.getItem('token');

                // 1. Get the temporary code from main backend
                const response = await fetch('https://vicktoria.ai/api/generate-jump-code', {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  }
                });
                const data = await response.json();

                // 2. Open the new tab with ONLY the code in the URL
                // 2. Redirect to VicktoriaAi with the one-time code
                if (data.code) {
                  window.location.href = `https://vicka.ai/api/auth/external?code=${data.code}`;
                }
              } catch (err) {
                console.error("Jump failed", err);
              }
            }}
          >
            Vicktoria
          </button>
        ) : (
          <button className="nav-btn-responsive" style={buttonStyle} onClick={() => navigate("/pricing")}>Pricing</button> // CHANGE
        )}

        {loggedInUser ? (
          <button className="nav-btn-responsive" style={buttonStyle} onClick={async () => {
            // 1. Tell Server to delete session/cookies
            await fetch('https://vicktoria.ai/api/logout', { method: 'POST', credentials: 'include' });

            // 2. Clear Local State (Existing logic)
            setLoggedInUser(null);
            localStorage.removeItem("loggedInUser");
            localStorage.removeItem("token");
            navigate("/");
          }}>Logout</button>
        ) : (
          <button className="nav-btn-responsive" style={buttonStyle} onClick={() => navigate("/login")}>Log In</button> // CHANGE
        )}

        {/* Contact Us toggle + popover. The inner wrapper is the ref boundary
            for the Esc / click-outside close handler (see effect above), and
            is deliberately margin-free so it hugs the button exactly — the
            popover anchors flush to it (see ContactForm). Horizontal spacing
            from the previous nav button lives on the outer spacer instead. */}
        <div className="contact-spacer-responsive" style={{ position: 'relative' }}>
          <div ref={contactWrapRef} style={{ position: 'relative' }}>
            <button
              className={`nav-btn-responsive contact-btn-responsive ${showContact ? 'is-open' : ''}`}
              style={buttonStyle}
              onClick={() => setShowContact(!showContact)}
            >
              Contact Us
            </button>
            {showContact && <ContactForm onClose={() => setShowContact(false)} />}
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;